import styled from 'styled-components';
import { COLORS } from '../../../../theme/Colors';

export const CalendarContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

export const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const NavigationButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #f3f4f6;
  }
`;

export const NavIcon = styled.span`
  font-size: 20px;
  color: #374151;
  font-weight: 600;
`;

export const MonthYear = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  min-width: 200px;
  text-align: center;
`;

export const ViewToggle = styled.div`
  display: flex;
  gap: 4px;
  background: #f3f4f6;
  padding: 4px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

export const ViewButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 6px 12px;
  background: ${props => props.$active ? 'white' : 'transparent'};
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.$active ? '#1f2937' : '#6b7280'};
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'};
  
  &:hover {
    color: #1f2937;
  }
`;

export const WeekDaysRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 6px;
`;

export const WeekDay = styled.div`
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  padding: 6px 0;
`;

export const DaysGrid = styled.div<{ $viewMode: 'week' | 'month' }>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: ${props => props.$viewMode === 'week' ? '3px' : '2px'};
  grid-auto-rows: ${props => props.$viewMode === 'week' ? 'minmax(60px, auto)' : 'auto'};
`;

export const DayCell = styled.div<{
  $isCurrentMonth: boolean;
  $isToday: boolean;
  $isSelected: boolean;
  $hasEvents: boolean;
  $viewMode: 'week' | 'month';
}>`
  aspect-ratio: ${props => props.$viewMode === 'month' ? '1' : 'auto'};
  min-height: ${props => props.$viewMode === 'week' ? '60px' : 'auto'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${props => props.$viewMode === 'week' ? 'space-between' : 'center'};
  cursor: pointer;
  border-radius: 8px;
  padding: ${props => props.$viewMode === 'week' ? '8px 6px' : '4px'};
  position: relative;
  transition: all 0.2s;
  background: ${props => {
    if (props.$isSelected) return COLORS.primary || '#6366f1';
    if (props.$isToday && !props.$isSelected) return '#fef3c7';
    if (props.$viewMode === 'week') return '#f9fafb';
    return 'transparent';
  }};
  border: ${props => {
    if (props.$viewMode === 'week') {
      if (props.$isSelected) return `2px solid ${COLORS.primary || '#6366f1'}`;
      return '1px solid #e5e7eb';
    }
    return 'none';
  }};
  
  &:hover {
    background: ${props => {
      if (props.$isSelected) return COLORS.primary || '#6366f1';
      if (props.$viewMode === 'week') return '#f3f4f6';
      return '#f3f4f6';
    }};
    border-color: ${props => props.$viewMode === 'week' && !props.$isSelected ? '#d1d5db' : ''};
  }
`;

export const DayHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2px;
  margin-bottom: 2px;
  width: 100%;
`;

export const DayNumber = styled.span<{ $isCurrentMonth?: boolean; $isToday?: boolean; $isSelected?: boolean }>`
  font-size: ${props => props.$isToday ? '13px' : '12px'};
  font-weight: ${props => props.$isToday ? '700' : '600'};
  color: ${props => {
    if (props.$isSelected) return 'white';
    if (props.$isToday) return COLORS.primary || '#6366f1';
    return props.$isCurrentMonth !== false ? '#1f2937' : '#d1d5db';
  }};
`;

export const DayName = styled.span<{ $isSelected?: boolean }>`
  font-size: 10px;
  color: ${props => props.$isSelected ? 'rgba(255, 255, 255, 0.9)' : '#6b7280'};
  font-weight: 400;
`;

export const TodayLabel = styled.span<{ $isSelected?: boolean }>`
  font-size: 9px;
  color: ${props => props.$isSelected ? 'white' : COLORS.primary || '#6366f1'};
  font-weight: 600;
  margin-left: auto;
  text-transform: uppercase;
`;

export const SessionCount = styled.div`
  background: white;
  color: ${COLORS.primary || '#6366f1'};
  font-size: 11px;
  font-weight: 600;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: auto;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

export const EventIndicators = styled.div`
  display: flex;
  gap: 2px;
  position: absolute;
  bottom: 4px;
`;

export const EventDot = styled.div<{ $status: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => {
    switch (props.$status) {
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return COLORS.primary || '#6366f1';
    }
  }};
  cursor: pointer;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.3);
  }
`;

export const MoreEvents = styled.span`
  font-size: 10px;
  color: #6b7280;
  font-weight: 600;
`;

export const SessionsPanel = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
`;

export const SessionsPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const SessionsPanelTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #1f2937;
  }
`;

export const SessionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }
`;

export const SessionCard = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  align-items: center;
  
  &:hover {
    background: #f3f4f6;
    transform: translateX(4px);
  }
`;

export const SessionTime = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
`;

export const SessionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const SessionClassName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
`;

export const SessionSubject = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

export const SessionMeta = styled.div`
  font-size: 12px;
  color: #9ca3af;
`;

export const SessionStatus = styled.div<{ $status: string }>`
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  background: ${props => {
    switch (props.$status) {
      case 'completed': return '#d1fae5';
      case 'cancelled': return '#fee2e2';
      default: return '#e0e7ff';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'completed': return '#065f46';
      case 'cancelled': return '#991b1b';
      default: return '#3730a3';
    }
  }};
`;

export const EmptySessionsPanel = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  text-align: center;
`;

export const EmptySessionsText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;